/**
 * Properties pages under the Product section — RO-3-05 (task #44298).
 *
 * Scope (docs/rollover/inventory/pages.md § Product):
 *   - properties/page.tsx            → /properties
 *   - properties/new/page.tsx        → /properties/new
 *   - properties/unattributed/page.tsx → /properties/unattributed
 *
 * Data plane:
 *   - Index + unattributed were Next server components (in-process core reads).
 *     Client routes call product.properties / product.unattributed.
 *   - New-property kind catalog was pure PROPERTY_KINDS/profileForKind — now
 *     product.kindOptions so the client never imports the core barrel (RO-4-04).
 *   - createTrackedProperty → projectActions.tracked (already mounted, RO-2-09b).
 *   - reclassifyUnattributedAction → properties.reclassify (already mounted).
 *
 * Live Next pages under excellent/app/properties/** are UNTOUCHED (D6).
 */

import * as React from "react";
import { useMemo, useState, useTransition } from "react";
import { createRoute } from "@tanstack/react-router";
import {
  PageHeader,
  Button,
  ColdStartChoices,
  FormShell,
  FormField,
  Input,
  Alert,
  AlertDescription,
  IntentBadge,
  DataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumn,
  formatRelativeTime,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";
import { apiCall, useApi } from "@excellent/ui/api";
import { RouterLink as Link, useNavigate } from "@excellent/ui/router";
import { product } from "@excellent/ui/api/contracts/product";
import type { ProductPropertyRow } from "@excellent/ui/api/contracts/product";
import { projectActions } from "@excellent/ui/api/contracts/project-actions";
import { properties as propertiesOps } from "@excellent/ui/api/contracts/database/properties";
import { rootRoute } from "./__root";

// ── /properties — index ─────────────────────────────────────────────────────

function PropertiesTable({ rows }: { rows: ProductPropertyRow[] }) {
  const navigate = useNavigate();
  const columns: DataTableColumn<ProductPropertyRow>[] = [
    { key: "name", header: "Product", render: (r) => r.name },
    {
      key: "kind",
      header: "Kind",
      render: (r) => <IntentBadge intent="neutral">{r.kindLabel}</IntentBadge>,
    },
    {
      key: "adoption",
      header: "Adoption",
      render: (r) =>
        r.adoption === "—" ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums">{r.adoption}</span>
        ),
    },
  ];
  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.slug}
      onRowClick={(r) => navigate.push(r.href)}
    />
  );
}

function PropertiesIndexPage() {
  const { data, isLoading } = useApi(product.properties, {});
  if (isLoading && !data) return null;
  if (!data) return null;

  const rows = data.rows;
  if (rows.length === 0) {
    return (
      <div className="w-full space-y-6 p-6">
        <PageHeader
          title="Properties"
          description="Track the products and assets you ship — and measure their adoption."
        />
        <ColdStartChoices
          icon="line-chart"
          title="Track your first product"
          description="Classify a product or asset to start measuring its adoption — installs, active users, downloads, signups."
          choices={[
            {
              key: "by-hand",
              icon: "plus",
              title: "Add a product by hand",
              body: "Name it, pick its kind, and log your current numbers. Takes a minute.",
              cta: "Track a product",
              href: "/properties/new",
              recommended: true,
            },
            {
              key: "connect",
              icon: "plug",
              title: "Connect an analytics source",
              body: "Bind Plausible, GA4, or PostHog to a property and its numbers flow in automatically.",
              cta: "Connect a source",
              href: "/settings/integrations",
            },
          ]}
          unlocks={{
            note: "Once a property has data, you'll see:",
            items: [
              "Live adoption KPIs",
              "A portfolio roll-up on your home",
              "Targets & on-plan banding",
            ],
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
      <PageHeader
        title="Properties"
        description={`${rows.length} tracked ${rows.length === 1 ? "product" : "products"} — each with the adoption KPIs its kind makes relevant.`}
        toolbar={
          <Button asChild size="icon-tb">
            <Link
              href="/properties/new"
              aria-label="Track a product"
              title="Track a product"
            >
              <Plus className="size-4" />
            </Link>
          </Button>
        }
      />
      <PropertiesTable rows={rows} />
    </div>
  );
}

export const propertiesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/properties",
  component: PropertiesIndexPage,
});

// ── /properties/new ─────────────────────────────────────────────────────────

function NewPropertyForm({
  kindOptions,
}: {
  kindOptions: {
    value: string;
    label: string;
    metrics: { metric: string; label: string; unit: string }[];
  }[];
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [domains, setDomains] = useState("");
  const [readingMetric, setReadingMetric] = useState("");
  const [readingValue, setReadingValue] = useState("");
  const [readingDate, setReadingDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const metrics = useMemo(
    () => kindOptions.find((o) => o.value === kind)?.metrics ?? [],
    [kindOptions, kind],
  );

  const domainList = domains
    .split(/[\s,]+/)
    .map((d) => d.trim())
    .filter(Boolean);

  const canCreate = name.trim().length > 0 && kind.length > 0;

  function create() {
    if (!canCreate) return;
    setError(null);
    const num = Number(readingValue);
    const firstReading =
      readingMetric && readingValue.trim() !== "" && Number.isFinite(num)
        ? { metric: readingMetric, value: num, sampledOn: readingDate }
        : null;
    startTransition(async () => {
      try {
        const res = await apiCall(projectActions.tracked, {
          body: {
            name: name.trim(),
            kind,
            domains: domainList,
            firstReading,
          },
        });
        if (res.ok && res.slug) {
          navigate.push(`/projects/${res.slug}/settings/tracking`);
        } else {
          setError(res.error ?? "Could not create the product. Try again.");
        }
      } catch {
        setError("Could not create the product. Try again.");
      }
    });
  }

  return (
    <FormShell
      footer={
        <Button size="sm" disabled={!canCreate || pending} onClick={create}>
          {pending ? "Creating…" : "Track product"}
        </Button>
      }
    >
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <FormField label="Name" help="What you call this product or asset.">
        <Input
          autoFocus
          placeholder="Excellent — Browser extension"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormField>

      <FormField
        label="Kind"
        help="What is it? This selects the adoption KPIs Excellent measures."
      >
        <div role="radiogroup" aria-label="Kind" className="flex flex-wrap gap-1.5">
          {kindOptions.map((o) => (
            <Button
              key={o.value}
              role="radio"
              aria-checked={kind === o.value}
              variant={kind === o.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setKind(o.value);
                setReadingMetric("");
              }}
            >
              {o.label}
            </Button>
          ))}
        </div>
      </FormField>

      {metrics.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 type-subtext text-muted-foreground">
          <span>Tracked KPIs</span>
          {metrics.map((m) => (
            <IntentBadge key={m.metric} intent="neutral">
              {m.label}
            </IntentBadge>
          ))}
        </div>
      ) : null}

      <FormField
        label="Domains"
        help="Hostnames this asset owns. A connected analytics source auto-attributes to it. Comma-separated. Optional."
      >
        <Input
          placeholder="excellent.so, app.excellent.so"
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
        />
      </FormField>

      {metrics.length > 0 ? (
        <FormField
          label="First reading"
          help="Optionally log today's number now — e.g. your current install count. You can always add more later."
        >
          <div className="space-y-2">
            <div role="radiogroup" aria-label="Metric" className="flex flex-wrap gap-1.5">
              {metrics.map((m) => (
                <Button
                  key={m.metric}
                  role="radio"
                  aria-checked={readingMetric === m.metric}
                  variant={readingMetric === m.metric ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    setReadingMetric(
                      readingMetric === m.metric ? "" : m.metric,
                    )
                  }
                >
                  {m.label}
                </Button>
              ))}
            </div>
            {readingMetric ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  value={readingValue}
                  onChange={(e) => setReadingValue(e.target.value)}
                />
                <Input
                  type="date"
                  value={readingDate}
                  onChange={(e) => setReadingDate(e.target.value)}
                />
              </div>
            ) : null}
          </div>
        </FormField>
      ) : null}
    </FormShell>
  );
}

function PropertiesNewPage() {
  const { data, isLoading } = useApi(product.kindOptions, {});
  if (isLoading && !data) return null;
  if (!data) return null;
  return (
    <div className="mx-auto w-full max-w-xl space-y-6 p-6">
      <PageHeader
        title="Track a new product"
        description="Add a product or asset you ship — an app, a site, an extension — and Excellent measures the adoption KPIs that matter for it."
      />
      <NewPropertyForm kindOptions={data.kinds} />
    </div>
  );
}

export const propertiesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/properties/new",
  component: PropertiesNewPage,
});

// ── /properties/unattributed ────────────────────────────────────────────────

type InboxRow = {
  table: string;
  kind: string;
  rowId: number;
  label: string;
  via: string | null;
  pending: boolean;
  createdAt: string | null;
};

const keyOf = (r: InboxRow) => `${r.table}:${r.rowId}`;

function UnattributedInbox({
  rows,
  properties,
  onPinned,
}: {
  rows: InboxRow[];
  properties: { uid: string; name: string }[];
  onPinned: () => void;
}) {
  const [pending, startTransition] = useTransition();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function pin(row: InboxRow, projectUid: string) {
    setError(null);
    setBusyKey(keyOf(row));
    startTransition(async () => {
      try {
        const res = await apiCall(propertiesOps.reclassify, {
          body: {
            table: row.table,
            rowId: row.rowId,
            projectUid,
          },
        });
        if (!res.ok) {
          setError(
            `Couldn't pin ${row.kind} #${row.rowId}: ${res.reason ?? "unknown error"}.`,
          );
        } else {
          onPinned();
        }
      } catch {
        setError(`Couldn't pin ${row.kind} #${row.rowId}.`);
      } finally {
        setBusyKey(null);
      }
    });
  }

  const columns: DataTableColumn<InboxRow>[] = [
    {
      key: "kind",
      header: "Source",
      width: "w-36",
      render: (r) => <span className="text-muted-foreground">{r.kind}</span>,
    },
    {
      key: "label",
      header: "Item",
      render: (r) => (
        <span className="block truncate" title={r.label}>
          {r.label}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-28",
      render: (r) => (
        <span className="type-subtext text-muted-foreground">
          {r.pending ? "pending" : (r.via ?? "unattributed")}
        </span>
      ),
    },
    {
      key: "added",
      header: "Seen",
      width: "w-28",
      render: (r) => (
        <span className="type-subtext text-muted-foreground">
          {r.createdAt ? formatRelativeTime(r.createdAt) : "—"}
        </span>
      ),
    },
    {
      key: "pin",
      header: "Pin to property",
      width: "w-56",
      render: (r) => (
        <Select
          disabled={pending && busyKey === keyOf(r)}
          onValueChange={(uid) => pin(r, uid)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Pin to property…" />
          </SelectTrigger>
          <SelectContent>
            {properties.map((p) => (
              <SelectItem key={p.uid} value={p.uid}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      {error ? <p className="type-body text-destructive">{error}</p> : null}
      {properties.length === 0 ? (
        <p className="type-body text-muted-foreground">
          No properties yet — create a top-level project (a property) before reclassifying.
        </p>
      ) : null}
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={keyOf}
        empty="Nothing unattributed — every signal is mapped to a property."
      />
    </div>
  );
}

function PropertiesUnattributedPage() {
  const { data, isLoading, mutate } = useApi(product.unattributed, {});
  if (isLoading && !data) return null;
  if (!data) return null;

  const rows = data.rows;
  const subtitle =
    rows.length === 0
      ? "Nothing unattributed — every signal is assigned to a property."
      : `${rows.length} signal${rows.length === 1 ? "" : "s"} the resolver couldn't map to a property — never a silent guess. Pin each to a property to fix it; a pin always wins and a later re-run never demotes it.`;

  return (
    <div className="space-y-4 p-6">
      <PageHeader title="Unattributed" description={subtitle} />
      <UnattributedInbox
        rows={rows}
        properties={data.properties}
        onPinned={() => mutate()}
      />
    </div>
  );
}

export const propertiesUnattributedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/properties/unattributed",
  component: PropertiesUnattributedPage,
});
